/**
 * The desktop frame (`lg`+) — a replica of the ManageEngine MDM console the
 * owner chose on 2026-09-26, replacing the Wallet rail:
 *
 *   ┌ brand bar ─ launcher · logo · product name ······ scope · version · avatar ┐
 *   ├ module bar ─ Home Account Loans ITR Rent Invest Reports Admin ··· 🔍 🔔 👁 ┤
 *   ├ sidebar ─────┬ breadcrumb ──────────────────────────────────────────────────┤
 *   │ + New Record │ page                                                        │
 *   │ ▾ Manage     │                                                             │
 *   └──────────────┴─────────────────────────────────────────────────────────────┘
 *
 * Three exports, laid out by `WalletShell`'s CSS grid: `DesktopTopBar`,
 * `DesktopSidebar`, `DesktopBreadcrumb`. All three are `hidden` below `lg`,
 * where the phone chrome (header + bottom bar) is untouched.
 *
 * WHAT IT MAY LINK TO — the module map in `modules.ts`, filtered by the same
 * visible page keys and admin flag the rail used. It draws; routes still guard.
 *
 * LIGHT-FIRST — the MDM design is light. While the frame is on screen a
 * `system` theme resolves to light (`setSystemFallback`); an explicit dark
 * choice in the avatar menu still wins. Cleared on unmount / below `lg`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Link from "@/compat/link";
import { ArrowLeftRight, ChevronDown, ChevronLeft, ChevronRight, Minus, Plus, Search } from "lucide-react";
import "@fontsource/lato/400.css";
import "@fontsource/lato/700.css";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { PrivacyToggle } from "@/lib/privacy/PrivacyToggle";
import { desktopLightDefault, setSystemFallback } from "@/lib/theme";
import { useAdminScope } from "@/lib/hooks/use-admin-scope";
import type { PageKey } from "@/lib/rbac/pages";
import { cn } from "@/lib/utils";
import { AvatarMenu } from "./AvatarMenu";
import { useWalletShell, type EntryType } from "./context";
import { useBreadcrumbTailValue } from "./crumbs";
import { activeItem, breadcrumbFor, moduleFor, visibleModules, type ModuleDef } from "./modules";
import "./desktop.css";

/** Mirrors the `@media (min-width: 1024px)` in `desktop.css` — one breakpoint. */
export const DESKTOP_QUERY = "(min-width: 1024px)";

const BUILD = typeof __AIM_BUILD__ !== "undefined" ? __AIM_BUILD__ : "dev";

const NEW_RECORD: { type: EntryType; label: string; key: string; Icon: typeof Plus; tone: string }[] = [
  { type: "income", label: "Income", key: "I", Icon: Plus, tone: "var(--wdf-ok)" },
  { type: "expense", label: "Expense", key: "E", Icon: Minus, tone: "var(--wdf-rose)" },
  { type: "transfer", label: "Transfer", key: "T", Icon: ArrowLeftRight, tone: "var(--wdf-accent)" },
];

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage blocked — the choice lasts this visit only */
  }
}

/** The modules this user sees — shared by the bar, sidebar and breadcrumb. */
export function useDesktopModules(visible: ReadonlySet<PageKey>, isAdmin: boolean): ModuleDef[] {
  return useMemo(() => visibleModules(visible, isAdmin), [visible, isAdmin]);
}

/** Collapsed sidebar, remembered per browser. */
export function useSidebarCollapsed(): [boolean, (next: boolean) => void] {
  const [collapsed, setCollapsed] = useState(() => readJson("aim-side-collapsed", false));
  return [
    collapsed,
    (next) => {
      setCollapsed(next);
      writeJson("aim-side-collapsed", next);
    },
  ];
}

/* ───────────────────────────── top bar ───────────────────────────── */

function ScopeSwitch() {
  const { viewAll, setViewAll } = useAdminScope();
  const queryClient = useQueryClient();
  const choose = (next: boolean) => {
    if (next === viewAll) return;
    setViewAll(next);
    void queryClient.invalidateQueries();
  };
  return (
    <div className="wdf-scope" role="group" aria-label="Data scope">
      <button type="button" aria-pressed={!viewAll} onClick={() => choose(false)}>
        Just mine
      </button>
      <button type="button" aria-pressed={viewAll} onClick={() => choose(true)}>
        All family
      </button>
    </div>
  );
}

function Launcher({ modules }: { modules: ModuleDef[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="wdf-launcher"
        aria-label="All AIM modules"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <i aria-hidden>
          {Array.from({ length: 9 }, (_, i) => (
            <b key={i} />
          ))}
        </i>
      </button>
      {open && (
        <div className="wdf-launcher-menu" role="menu">
          {modules.map((m) => (
            <Link key={m.key} href={m.href} role="menuitem">
              <span className="wdf-launcher-tile">{m.label.charAt(0)}</span>
              {m.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function DesktopTopBar({
  user,
  modules,
  onOpenSearch,
}: {
  user: { name: string; role: string };
  modules: ModuleDef[];
  onOpenSearch(): void;
}) {
  const { pathname } = useLocation();
  const current = moduleFor(pathname, modules);
  const isAdmin = user.role === "ADMIN";

  // Light-first while the frame is ACTUALLY on screen. The element is always
  // mounted (CSS hides it below `lg`), so this watches the breakpoint rather
  // than mounting — a phone must keep following its OS.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const query = window.matchMedia(DESKTOP_QUERY);
    const sync = () => setSystemFallback(query.matches && desktopLightDefault() ? "light" : null);
    sync();
    query.addEventListener("change", sync);
    return () => {
      query.removeEventListener("change", sync);
      setSystemFallback(null);
    };
  }, []);

  return (
    <div className="wdf-top hidden lg:block">
      <div className="wdf-brand">
        <Launcher modules={modules} />
        <Link href="/dashboard" className="wdf-logo">
          <span className="wdf-mark" aria-hidden>
            A
          </span>
          <span>
            <span className="wdf-t1">Comprint</span>
            <span className="wdf-t2">AIM — Accounts &amp; Investment Manager</span>
          </span>
        </Link>
        <div className="wdf-brand-right">
          {isAdmin && <ScopeSwitch />}
          <span className="wdf-ver">Version : {BUILD}</span>
          <AvatarMenu name={user.name} role={user.role} placement="down" />
        </div>
      </div>

      <nav aria-label="Modules" className="wdf-modnav">
        {modules.map((m) => (
          <Link
            key={m.key}
            href={m.href}
            aria-current={current?.key === m.key ? "page" : undefined}
          >
            {m.label}
          </Link>
        ))}
        <div className="wdf-tools">
          <button type="button" onClick={onOpenSearch} aria-label="Search" className="wdf-tool">
            <Search className="h-[18px] w-[18px]" />
          </button>
          <NotificationBell />
          <PrivacyToggle variant="chrome" />
        </div>
      </nav>
    </div>
  );
}

/* ───────────────────────────── sidebar ───────────────────────────── */

function NewRecordButton({ onAdd }: { onAdd(type: EntryType): void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") return setOpen(false);
      const hit = NEW_RECORD.find((r) => r.key === e.key.toUpperCase());
      if (hit) {
        e.preventDefault();
        setOpen(false);
        onAdd(hit.type);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onAdd]);

  return (
    <div className="wdf-newrec" ref={ref}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Plus className="h-4 w-4" strokeWidth={2.5} aria-hidden />
        New Record
        <ChevronDown className="ml-auto h-3.5 w-3.5" aria-hidden />
      </button>
      {open && (
        <div className="wdf-menu" role="menu" aria-label="New record">
          {NEW_RECORD.map(({ type, label, key, Icon, tone }) => (
            <button
              key={type}
              type="button"
              role="menuitem"
              aria-label={`New ${type}`}
              onClick={() => {
                setOpen(false);
                onAdd(type);
              }}
            >
              <b style={{ background: tone }}>
                <Icon className="h-3 w-3" strokeWidth={3} aria-hidden />
              </b>
              {label}
              <small>{key}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function DesktopSidebar({
  modules,
  canAdd,
  onAdd,
  collapsed,
  onCollapse,
}: {
  modules: ModuleDef[];
  canAdd: boolean;
  onAdd(type: EntryType): void;
  collapsed: boolean;
  onCollapse(next: boolean): void;
}) {
  const { pathname, search } = useLocation();
  const { accounts } = useWalletShell();
  const current = moduleFor(pathname, modules);
  const active = activeItem(pathname, search, current);
  const [closed, setClosed] = useState<string[]>(() => readJson("aim-side-closed", []));

  const toggle = (id: string) => {
    const next = closed.includes(id) ? closed.filter((c) => c !== id) : [...closed, id];
    setClosed(next);
    writeJson("aim-side-closed", next);
  };

  const counts: Record<string, number | undefined> = {
    "/accounts": accounts.length || undefined,
  };

  return (
    <aside className="wdf-side hidden lg:block" data-collapsed={collapsed || undefined}>
      <div className="wdf-side-scroll">
        {current?.newRecord && canAdd && <NewRecordButton onAdd={onAdd} />}
        {current?.groups.map((g) => {
          const id = `${current.key}:${g.title}`;
          const isClosed = closed.includes(id);
          return (
            <div key={id} className="wdf-grp">
              <button
                type="button"
                className="wdf-grp-h"
                aria-expanded={!isClosed}
                onClick={() => toggle(id)}
              >
                <ChevronDown
                  className={cn("h-3 w-3 transition-transform", isClosed && "-rotate-90")}
                  aria-hidden
                />
                {g.title}
              </button>
              {!isClosed && (
                <ul>
                  {g.items.map((item) => (
                    <li key={item.href}>
                      <Link
                        href={item.href}
                        aria-current={active === item ? "page" : undefined}
                      >
                        <span className="truncate">{item.label}</span>
                        {counts[item.href] !== undefined && (
                          <span className="wdf-n">{counts[item.href]}</span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
      <button
        type="button"
        className="wdf-collapse"
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        onClick={() => onCollapse(!collapsed)}
      >
        {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
      </button>
    </aside>
  );
}

/* ─────────────────────────── breadcrumb ──────────────────────────── */

export function DesktopBreadcrumb({ modules }: { modules: ModuleDef[] }) {
  const { pathname, search } = useLocation();
  const tail = useBreadcrumbTailValue();
  const crumbs = breadcrumbFor(pathname, search, modules, tail);
  if (crumbs.length === 0) return null;
  return (
    <nav aria-label="Breadcrumb" className="wdf-crumb hidden lg:flex">
      {crumbs.map((c, i) => (
        <span key={`${c.label}-${i}`} className="flex items-center gap-2">
          {i > 0 && <span aria-hidden>›</span>}
          {c.href ? (
            <Link href={c.href}>{c.label}</Link>
          ) : (
            <span className="wdf-cur" aria-current="page">
              {c.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}

/* ───────────────────────── keyboard: new record ──────────────────────── */

/** True when a key press belongs to whatever the user is typing into. */
export function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

/**
 * Desktop-only single keys that open the New Record form from any page:
 * E (or N) expense, I income, T transfer — the letters the sidebar menu shows.
 * Ignored while typing, with a modifier held, or while any dialog is open.
 */
export function useNewRecordShortcuts(enabled: boolean, onAdd: (type: EntryType) => void): void {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
      if (isTypingTarget(e.target)) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"], [role="menu"]')) return;
      const type = ({ e: "expense", n: "expense", i: "income", t: "transfer" } as Record<string, EntryType>)[
        e.key.toLowerCase()
      ];
      if (!type) return;
      if (!window.matchMedia?.(DESKTOP_QUERY).matches) return;
      e.preventDefault();
      onAdd(type);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [enabled, onAdd]);
}
